'use client';

import type { ReactNode } from 'react';
import { ToolError } from '@/lib/kit-tools-client';

export const inputClass =
  'w-full rounded-xl border border-line-2 bg-veil-2 px-4 py-3 text-[0.95rem] text-mist-100 placeholder:text-mist-500 transition-colors focus:border-spark-500/60 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-spark-500';

/**
 * The bot trap. Real visitors never see or reach it: it is moved off-screen
 * (not display:none, which some bots skip) and taken out of the tab order and
 * the accessibility tree. A bot that fills it gets the same success response a
 * person gets, and nothing is saved.
 */
export function Honeypot({ name, value, onChange }: { name: string; value: string; onChange: (v: string) => void }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
      <label>
        Leave this field empty
        <input type="text" name={name} tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}

export function Field({ id, label, required, hint, error, children }: { id: string; label: string; required?: boolean; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-mist-100">
        {label}
        {required ? <span className="text-mist-500"> (required)</span> : <span className="text-mist-500"> (optional)</span>}
      </label>
      {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-mist-500">{hint}</p>}
      <div className="mt-2">{children}</div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** What to tell the visitor when a KIT call fails. 429 is a rate limit, not a fault. */
export function failureMessage(e: unknown): string {
  if (e instanceof ToolError && e.status === 429) return 'Too many attempts from this connection. Please wait a few minutes and try again.';
  return 'That did not go through. Please try again, or email sales@kitagency.co.';
}

export function Spinner() {
  return <span aria-hidden="true" className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />;
}

export function optionValue(o: string | { value: string; label?: string }) {
  return typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value };
}
